// Borrowed from https://github.com/okikio/sharedworker/blob/main/src/index.ts
/**
 * indicates if SharedWorker is supported, in the global scope
 */
export const SharedWorkerSupported = 'SharedWorker' in globalThis;
/**
 * A polyfill class for `SharedWorker`, it accepts a URL/string as well as any other options the spec. allows for `SharedWorker`.
 * It supports all the same methods and properties as the original, except it adds compatibility methods and properties for older browsers that don't support `SharedWorker`, so, it can switch to normal `Workers` instead.
 */
export class SharedWorkerPolyfill {
    constructor(url, opts) {
        if (SharedWorkerSupported) {
            this.ActualWorker = new SharedWorker(url, opts);
        }
        else {
            this.ActualWorker = new Worker(url, opts);
        }
    }
    /**
     * An EventListener called when MessageEvent of type message is fired on the port—that is, when the port receives a message.
     */
    get onmessage() {
        var _a;
        if (SharedWorkerSupported) {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.onmessage;
        }
        return this.ActualWorker.onmessage;
    }
    set onmessage(value) {
        if (SharedWorkerSupported) {
            this.ActualWorker.port.onmessage = value;
        }
        else {
            this.ActualWorker.onmessage = value;
        }
    }
    /**
     * An EventListener called when a MessageEvent of type MessageError is fired—that is, when it receives a message that cannot be deserialized.
     */
    get onmessageerror() {
        var _a;
        if (SharedWorkerSupported) {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.onmessageerror;
        }
        return this.ActualWorker.onmessageerror;
    }
    set onmessageerror(value) {
        if (SharedWorkerSupported) {
            this
                .ActualWorker.port.onmessageerror = value;
        }
        else {
            this.ActualWorker.onmessageerror = value;
        }
    }
    /**
     * Starts the sending of messages queued on the port (only needed when using EventTarget.addEventListener; it is implied when using MessagePort.onmessage.)
     */
    start() {
        var _a;
        if (SharedWorkerSupported) {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.start();
        }
    }
    /**
     * Clones message and transmits it to worker's global environment. transfer can be passed as a list of objects that are to be transferred rather than cloned.
     */
    postMessage(message, transfer) {
        var _a;
        if (SharedWorkerSupported) {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.postMessage(message, transfer);
        }
        return this.ActualWorker.postMessage(message, transfer);
    }
    /**
     * Immediately terminates the worker. This does not let worker finish its operations; it is halted at once. ServiceWorker instances do not support this method.
     */
    terminate() {
        var _a;
        if (SharedWorkerSupported) {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.close();
        }
        return this.ActualWorker.terminate();
    }
    /**
     * Disconnects the port, so it is no longer active.
     */
    close() {
        return this.terminate();
    }
    /**
     * Returns a MessagePort object used to communicate with and control the shared worker.
     */
    get port() {
        return (SharedWorkerSupported
            ? this.ActualWorker.port
            : this.ActualWorker);
    }
    /**
     * Is an EventListener that is called whenever an ErrorEvent of type error event occurs.
     */
    get onerror() {
        return this.ActualWorker.onerror;
    }
    set onerror(value) {
        this.ActualWorker.onerror = value;
    }
    addEventListener(type, listener, options) {
        var _a;
        if (SharedWorkerSupported && type !== 'error') {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.addEventListener(type, listener, options);
        }
        return this.ActualWorker.addEventListener(type, listener, options);
    }
    removeEventListener(type, listener, options) {
        var _a;
        if (SharedWorkerSupported && type !== 'error') {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.removeEventListener(type, listener, options);
        }
        return this.ActualWorker.removeEventListener(type, listener, options);
    }
    /**
     * Dispatches an event to this EventTarget.
     */
    dispatchEvent(event) {
        return this.ActualWorker.dispatchEvent(event);
    }
}
export default SharedWorkerPolyfill;
